/**
 * Futures-Spot Page
 *
 * Page component for displaying real-time futures-spot arbitrage opportunities.
 * Features:
 * - Integrated FuturesSpotDataProvider for data management
 * - Connection status indicators for all exchanges
 * - Refresh button for manual data reload
 * - Mobile-responsive layout
 * - Cyberpunk theme styling
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Paper,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
import { FuturesSpotDataProvider, useFuturesSpotData } from '../contexts/FuturesSpotDataContext';
import { FuturesSpotTable } from '../components/FuturesSpotTable';
import { CcxtTest } from '../components/CcxtTest';
import { getExchangeLogo, EXCHANGE_NAMES } from '../utils/exchangeUrls';
import { HeaderStats } from '../components/HeaderStats';

/**
 * Get exchange index from exchange name
 */
const getExchangeIndex = (exchangeName: string): number => {
  // Map connection status keys to exchange names
  const nameMap: Record<string, string> = {
    'binanceFutures': 'binance',
    'binanceSpot': 'binance',
    'upbit': 'upbit',
    'bithumb': 'bithumb',
  };

  const normalizedName = nameMap[exchangeName] || exchangeName.toLowerCase();
  return EXCHANGE_NAMES.indexOf(normalizedName as any);
};

/**
 * Page content component (must be inside provider)
 */
const FuturesSpotPageContent: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state } = useFuturesSpotData();

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    window.location.reload();
  };

  /**
   * Get connection status color
   */
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'connected':
        return theme.palette.success.main;
      case 'reconnecting':
        return theme.palette.warning.main;
      case 'disconnected':
        return theme.palette.error.main;
      default:
        return theme.palette.text.disabled;
    }
  };

  /**
   * Get connection status label
   */
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Main Title */}
      <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontSize: { xs: '1.5rem', sm: '2.75rem', md: '3.75rem' },
            fontWeight: 'bold',
            letterSpacing: { xs: '0.05em', sm: '0.1em' },
            textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 40px rgba(0, 255, 255, 0.8)',
            animation: 'gradient 3s ease infinite',
            '@keyframes gradient': {
              '0%': { backgroundPosition: '0% 50%' },
              '50%': { backgroundPosition: '100% 50%' },
              '100%': { backgroundPosition: '0% 50%' },
            },
            mb: 1,
          }}
        >
          {t('app.title').toUpperCase()}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.875rem', md: '1rem' },
            color: 'text.secondary',
            letterSpacing: { xs: '0.05em', sm: '0.2em' },
            textTransform: 'uppercase',
            px: { xs: 2, sm: 0 },
          }}
        >
          {t('app.subtitle')}
        </Typography>
      </Box>

      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}30 0%, ${theme.palette.secondary.dark}30 100%)`,
          borderRadius: 2,
          border: `1px solid ${theme.palette.primary.main}40`,
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
          spacing={2}
        >
          {/* Title */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUpIcon
                sx={{
                  fontSize: 32,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                fontWeight="bold"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Futures-Spot Arbitrage
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Real-time price differentials across spot and futures markets
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  background: `${theme.palette.primary.main}20`,
                  '&:hover': {
                    background: `${theme.palette.primary.main}40`,
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        {/* Connection Status Indicators */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            {/* Binance Futures */}
            <Tooltip title={`Binance Futures: ${getStatusLabel(state.connectionStatus.binanceFutures)}`} arrow>
              <Box
                sx={{
                  border: `2px solid ${getStatusColor(state.connectionStatus.binanceFutures)}`,
                  boxShadow: `0 0 6px ${getStatusColor(state.connectionStatus.binanceFutures)}60`,
                  borderRadius: '50%',
                  display: 'inline-block',
                }}
              >
                <Avatar
                  src={getExchangeLogo(getExchangeIndex('binanceFutures'))}
                  sx={{
                    width: 32,
                    height: 32,
                    filter: 'grayscale(100%)',
                  }}
                />
              </Box>
            </Tooltip>

            {/* Binance Spot */}
            <Tooltip title={`Binance Spot: ${getStatusLabel(state.connectionStatus.binanceSpot)}`} arrow>
              <Avatar
                src={getExchangeLogo(getExchangeIndex('binanceSpot'))}
                sx={{
                  width: 32,
                  height: 32,
                  border: `2px solid ${getStatusColor(state.connectionStatus.binanceSpot)}`,
                  boxShadow: `0 0 6px ${getStatusColor(state.connectionStatus.binanceSpot)}60`,
                }}
              />
            </Tooltip>

            {/* Upbit */}
            <Tooltip title={`Upbit: ${getStatusLabel(state.connectionStatus.upbit)}`} arrow>
              <Avatar
                src={getExchangeLogo(getExchangeIndex('upbit'))}
                sx={{
                  width: 32,
                  height: 32,
                  border: `2px solid ${getStatusColor(state.connectionStatus.upbit)}`,
                  boxShadow: `0 0 6px ${getStatusColor(state.connectionStatus.upbit)}60`,
                }}
              />
            </Tooltip>

            {/* Bithumb */}
            <Tooltip title={`Bithumb: ${getStatusLabel(state.connectionStatus.bithumb)}`} arrow>
              <Avatar
                src={getExchangeLogo(getExchangeIndex('bithumb'))}
                sx={{
                  width: 32,
                  height: 32,
                  border: `2px solid ${getStatusColor(state.connectionStatus.bithumb)}`,
                  boxShadow: `0 0 6px ${getStatusColor(state.connectionStatus.bithumb)}60`,
                }}
              />
            </Tooltip>
          </Stack>
        </Box>

        {/* Error Messages */}
        {state.errors.size > 0 && (
          <Box sx={{ mt: 2 }}>
            {Array.from(state.errors.entries()).map(([exchange, error]) => (
              <Typography
                key={exchange}
                variant="caption"
                color="error"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {exchange}: {error}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>

      {/* Header Statistics Bar - BTC prices and market stats */}
      <HeaderStats />

      {/* Table */}
      <FuturesSpotTable />

      {/* Footer Info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          Data updates every 30ms (33 fps) for smooth real-time display
        </Typography>
      </Box>
    </Container>
  );
};

/**
 * Futures-Spot Page Component (with provider)
 */
export const FuturesSpotPage: React.FC = () => {
  return (
    <FuturesSpotDataProvider>
      <FuturesSpotPageContent />
    </FuturesSpotDataProvider>
  );
};

export default FuturesSpotPage;
